import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdSearch, MdSave } from "react-icons/md";
import { modelAssignmentApi, ModelAssignmentPayload, ModelAssignmentDetail, availableModelsApi, AvailableModel } from "api/models";
import { useAuthContext } from "auth/AuthProvider";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

// Task Type Enum values
const TASK_TYPES = {
  DECOMPOSE: "DECOMPOSE",
  PLAN: "PLAN", 
  EXECUTE: "EXECUTE",
  SYNTHESIZE: "SYNTHESIZE",
  GENERATE_TITLE: "GENERATE_TITLE",
  WORKFLOW_RETRIEVE: "WORKFLOW_RETRIEVE",
  RESOURCE_GENERATE: "RESOURCE_GENERATE",
  IMAGE_MODEL: "IMAGE_MODEL"
} as const;

// Display names for UI - will be translated in component
const TASK_DISPLAY_NAMES_KEYS: Record<string, string> = {
  DECOMPOSE: "settings.taskTypes.decompose",
  PLAN: "settings.taskTypes.plan",
  EXECUTE: "settings.taskTypes.execute", 
  SYNTHESIZE: "settings.taskTypes.synthesize",
  GENERATE_TITLE: "settings.taskTypes.generateTitle",
  WORKFLOW_RETRIEVE: "settings.taskTypes.retrieveWorkflow",
  RESOURCE_GENERATE: "settings.taskTypes.generateResource",
  IMAGE_MODEL: "settings.taskTypes.imageModel"
};


interface TaskAssignment {
  id: string;
  task: string; // This will be the enum value (e.g., "DECOMPOSE")
  selectedModel: string; // This will be the model ID
}

interface EmbeddingAssignment {
  id: string;
  task: string; // "EMBEDDING"
  selectedModel: string; // This will be the model ID
}

interface ModelAssignmentPageProps {
  settingsType: "global" | "organization";
}

const ModelAssignmentPage: React.FC<ModelAssignmentPageProps> = ({ settingsType }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [taskAssignments, setTaskAssignments] = useState<TaskAssignment[]>([]);
  const [embeddingAssignment, setEmbeddingAssignment] = useState<EmbeddingAssignment>({
    id: "embedding-1",
    task: "EMBEDDING",
    selectedModel: "inherit" // Default to inherit
  });
  const [availableLLMModels, setAvailableLLMModels] = useState<AvailableModel[]>([]);
  const [availableEmbeddingModels, setAvailableEmbeddingModels] = useState<AvailableModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialize with default task assignments if none exist
  const initializeDefaultAssignments = (): TaskAssignment[] => {
    return Object.values(TASK_TYPES).map((taskType, index) => ({
      id: `task-${index + 1}`,
      task: taskType,
      selectedModel: "inherit" // Default to inherit
    }));
  };

  // Fetch available models and assignments on component mount
  useEffect(() => {
    fetchAvailableModels();
    fetchAssignments();
  }, [settingsType]);

  // Fetch model assignment details
  const fetchModelAssignmentDetails = async (): Promise<{ task_types: Record<string, string | null>; embedding_model_id: string | null; }> => {
    if (settingsType === "global") {
      return await modelAssignmentApi.global.get();
    }
    const organizationId = user?.current_organization?.id;
    if (!organizationId) {
      throw new Error("No organization selected");
    }
    return await modelAssignmentApi.organization.get(organizationId);
  };

  const fetchAvailableModels = async () => {
    try {
      let llmModels: AvailableModel[] = [];
      let embeddingModels: AvailableModel[] = [];
      
      if (settingsType === "global") {
        llmModels = await availableModelsApi.global.getLLMModels();
        embeddingModels = await availableModelsApi.global.getEmbeddingModels();
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        llmModels = await availableModelsApi.organization.getLLMModels(organizationId);
        embeddingModels = await availableModelsApi.organization.getEmbeddingModels(organizationId);
      }
      
      setAvailableLLMModels(llmModels);
      setAvailableEmbeddingModels(embeddingModels);
    } catch (err) {
      handleAxiosAndCaughtError(err);
      setError(t("settings.failedToFetchAvailableModels"));
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch model assignment details from mock API
      const details = await fetchModelAssignmentDetails();
      
      // Convert details to task assignments
      const assignments: TaskAssignment[] = Object.values(TASK_TYPES).map((taskType, index) => ({
        id: `task-${index + 1}`,
        task: taskType,
        selectedModel: details.task_types[taskType] || "inherit" // Use "inherit" if null
      }));
      
      setTaskAssignments(assignments);
      
      // Set embedding assignment
      setEmbeddingAssignment(prev => ({
        ...prev,
        selectedModel: details.embedding_model_id || "inherit" // Use "inherit" if null
      }));
      
    } catch (err) {
      handleAxiosAndCaughtError(err);
      setError(t("settings.failedToFetchModelAssignments"));
      // Initialize with defaults on error
      setTaskAssignments(initializeDefaultAssignments());
    } finally {
      setLoading(false);
    }
  };


  const filteredAssignments = taskAssignments.filter((assignment) => {
    const displayName = t(TASK_DISPLAY_NAMES_KEYS[assignment.task] || assignment.task) || assignment.task;
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleModelChange = (taskId: string, newModel: string) => {
    setTaskAssignments(prev => 
      prev.map(assignment => 
        assignment.id === taskId 
          ? { ...assignment, selectedModel: newModel }
          : assignment
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Create task_types object from task assignments
      const taskTypes: Record<string, string | null> = {};
      taskAssignments.forEach(assignment => {
        // Send null if "inherit" is selected, otherwise send model ID
        taskTypes[assignment.task] = assignment.selectedModel === "inherit" ? null : assignment.selectedModel;
      });
      
      // Create payload according to the specified format
      const payload: ModelAssignmentPayload = {
        task_types: {
          DECOMPOSE: taskTypes.DECOMPOSE,
          PLAN: taskTypes.PLAN,
          EXECUTE: taskTypes.EXECUTE,
          SYNTHESIZE: taskTypes.SYNTHESIZE,
          GENERATE_TITLE: taskTypes.GENERATE_TITLE,
          WORKFLOW_RETRIEVE: taskTypes.WORKFLOW_RETRIEVE,
          RESOURCE_GENERATE: taskTypes.RESOURCE_GENERATE,
          IMAGE_MODEL: taskTypes.IMAGE_MODEL
        },
        embedding_model_id: embeddingAssignment.selectedModel === "inherit" ? null : embeddingAssignment.selectedModel
      };
      
      if (settingsType === "global") {
        await modelAssignmentApi.global.save(payload);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await modelAssignmentApi.organization.save(organizationId, payload);
      }
      
      // Show success message or refresh data
      console.log(`${settingsType} model assignments saved successfully`);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card extra="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t("settings.loadingModelAssignments")}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card extra="p-6">
          <div className="text-center py-8">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchAssignments}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t("common.retry")}
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card extra="p-6">
        {/* Header with Search and Save Button */}
        <div className="mb-6 flex items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t("settings.searchModelAssignment")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white dark:focus:border-blue-400"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="ml-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            <MdSave className="h-4 w-4" />
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>

        {/* Task Model Configuration Section */}
        <div className="mb-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("settings.taskModelConfiguration")}
          </h3>
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 dark:bg-gray-800">
              <div className="grid grid-cols-2 gap-4">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("settings.task")}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("settings.llmModel")}
                </span>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-800">
              {filteredAssignments.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  {t("settings.noTaskAssignmentsFound")}
                </div>
              ) : (
                filteredAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="grid grid-cols-2 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <span className="flex items-center text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t(TASK_DISPLAY_NAMES_KEYS[assignment.task] || assignment.task)}
                    </span>
                    <div className="flex items-center">
                      <select
                        value={assignment.selectedModel}
                        onChange={(e) => handleModelChange(assignment.id, e.target.value)}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="inherit">{t("settings.inherit")}</option>
                        {availableLLMModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Embedding Model Configuration Section */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("settings.embeddingModelConfiguration")}
          </h3>
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 dark:bg-gray-800">
              <div className="grid grid-cols-2 gap-4">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("settings.task")}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("settings.embeddingModel")}
                </span>
              </div>
            </div>

            {/* Table Body */}
            <div className="bg-white dark:bg-navy-800">
              <div className="grid grid-cols-2 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <span className="flex items-center text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("settings.embedding")}
                    </span>
                    <div className="flex items-center">
                      <select
                    value={embeddingAssignment.selectedModel}
                    onChange={(e) => setEmbeddingAssignment(prev => ({ ...prev, selectedModel: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="inherit">{t("settings.inherit")}</option>
                        {availableEmbeddingModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ModelAssignmentPage;
